import { useRef } from "react";
import { AgGridReact } from "ag-grid-react";

// Context Imports
import { useAppContext } from "@/context/context";

// Layout Imports
import { Loading } from "../../layout";

// Component Imports
import StopAlertModal from "../../components/Modals/AlertManager/StopAlertModal";
import FormModal from "./components/FormModal/FormModal";
import GroupDestinationsModal from "./components/GroupDestinationsModal/GroupDestinationsModal";
import { AGTable } from "../../components";
import DefaultConfigModal from "@/components/Modals/AlertManager/DefaultConfigModal";

// Styling Imports
import "@pulse-ui/styling/pulse-wave-lib/index.scss";
import "@pulse-ui/styling/index.scss";
import "./index.scss";

// MUI Imports
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

// Hooks
import { useAlertManager } from "./hooks/useAlertManager";
import { useAlertManagerTable } from "./hooks/useAlertManagerTable";
import { useFormModal } from "./hooks/useFormModal";
import { useStopAlertModal } from "./hooks/useStopAlertModal";
import { useDefaultConfigModal } from "./hooks/useDefaultConfigModal";
import { useGroupDestinationsModal } from "./hooks/useGroupDestinationsModal";
import { transformCheckType } from "./utils/transformers";

const AlertManager = () => {
  const { context_state } = useAppContext();
  const gridRef = useRef<AgGridReact>(null);

  const {
    schemas,
    rowData,
    apiKeys,
    portfolios,
    portfolioSelectOptions,
    slackChannels,
    telegramChannels,
    smsChannels,
    groupDestinationNames,
    loading,
  } = useAlertManager();

  const formModal = useFormModal();
  const stopModal = useStopAlertModal();
  const defaultConfigModal = useDefaultConfigModal();
  const groupModal = useGroupDestinationsModal();

  const { colDefs, defaultColDef, autoGroupColumnDef, sideBar, getContextMenuItems } =
    useAlertManagerTable({
      onTestFire: formModal.handleTestFire,
      onEdit: formModal.openEdit,
      onDuplicate: formModal.openDuplicate,
      onStop: stopModal.openModal,
    });

  return (
    <div
      className="pulse-sidenav-adjusted-container pulse-bg-color--background-900"
      id="alert-app"
    >
      <div className="header-container">
        <div className="pulse-row">
          <p className="pulse-typography-headline-6 pulse-color--background-50">
            All Alerts
          </p>
        </div>
        <div className="header-container_right">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={formModal.openStart}
            className="header-container_right__button"
          >
            Start Alert
          </Button>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={groupModal.openModal}
            className="header-container_right__button"
            style={{ marginLeft: "1rem" }}
          >
            Destination Groups
          </Button>

          <Button
            variant="outlined"
            startIcon={<NotificationsActiveIcon />}
            onClick={defaultConfigModal.openModal}
            className="header-container_right__button"
            style={{ marginLeft: "1rem" }}
          >
            Start Default Config
          </Button>
        </div>
      </div>

      <AGTable
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        autoGroupColumnDef={autoGroupColumnDef}
        defaultColDef={defaultColDef}
        groupDisplayType="singleColumn"
        sideBar={sideBar}
        animateRows
        getContextMenuItems={getContextMenuItems}
      />

      <div className="pulse-pb-3">
        {/* FORM MODAL */}
        {schemas && (
          <FormModal
            open={formModal.open}
            handleClose={formModal.close}
            schemas={schemas}
            selectedSchemaName={formModal.selectedSchemaName}
            setSelectedSchemaName={formModal.setSelectedSchemaName}
            apiKeys={apiKeys}
            portfolios={portfolios}
            org_metadata={context_state?.org_metadata || {}}
            handleSubmit={formModal.handleSubmit}
            preset={formModal.preset}
            setPreset={formModal.setPreset}
            slackChannels={slackChannels}
            telegramChannels={telegramChannels}
            smsChannels={smsChannels}
            assetOptions={[]}
            groupDestinationNames={groupDestinationNames}
            transformCheckType={transformCheckType}
          />
        )}

        {/* DESTINATION GROUPS MODAL */}
        <GroupDestinationsModal
          open={groupModal.open}
          close={groupModal.closeModal}
          slackChannels={slackChannels}
          telegramChannels={telegramChannels}
          smsChannels={smsChannels}
        />

        {/* STOP ALERT MODAL */}
        {stopModal.open && stopModal.alertToStop && (
          <StopAlertModal
            open={stopModal.open}
            handleOpen={stopModal.closeModal}
            alert_name={stopModal.alertToStop}
            handleStopAlert={stopModal.handleStop}
          />
        )}

        {/* DEFAULT CONFIG MODAL */}
        {defaultConfigModal.open && (
          <DefaultConfigModal
            open={defaultConfigModal.open}
            handleOpen={defaultConfigModal.closeModal}
            accounts={apiKeys}
            portfolioSelectOptions={portfolioSelectOptions}
            handleStartDefaultConfig={defaultConfigModal.handleStartDefaultConfig}
          />
        )}
      </div>

      {loading && <Loading />}
    </div>
  );
};

export default AlertManager;
